import React, { useState, useEffect, useRef } from 'react';
import './StudentMessages.css';

const StudentMessages = ({ studentId, studentName }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewConvDialog, setShowNewConvDialog] = useState(false);
  const [courses, setCourses] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [newConvData, setNewConvData] = useState({
    course_id: '',
    subject: '',
  });
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  // Fetch conversations
  useEffect(() => {
    if (studentId) {
      fetchConversations();
    }
  }, [studentId]);

  // Fetch messages when conversation selected
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.conversation_id);

      // Poll every 5 seconds
      const interval = setInterval(() => {
        fetchMessages(selectedConversation.conversation_id);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [selectedConversation]);

  // Auto scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch student's courses for new conversation
  useEffect(() => {
    if (showNewConvDialog) {
      fetchStudentCourses();
    }
  }, [showNewConvDialog]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const fetchConversations = async () => {
    if (!studentId) return [];
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        student_id: studentId,
        limit: 50,
      });

      const response = await fetch(`/api/messages/conversations/student?${params}`);
      const data = await response.json();

      if (response.ok) {
        const list = data.conversations || [];
        setConversations(list);
        return list;
      } else {
        setError(data.error || 'Failed to fetch conversations');
        return [];
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    if (!conversationId) return;
    try {
      const response = await fetch(`/api/messages/conversation/${conversationId}`);
      const data = await response.json();

      if (response.ok) {
        setMessages(data.messages || []);

        // Mark as read
        markConversationAsRead(conversationId);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };

  const markConversationAsRead = async (conversationId) => {
    if (!conversationId || !studentId) return;
    try {
      await fetch('/api/messages/mark-read', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          reader_id: studentId,
        }),
      });

      // Update local state
      setConversations(prev =>
        prev.map(conv =>
          conv.conversation_id === conversationId
            ? { ...conv, unread_count: 0 }
            : conv
        )
      );
    } catch (err) {
      console.error('Error marking conversation as read:', err);
    }
  };

  const fetchStudentCourses = async () => {
    if (!studentId) return;
    try {
      // Reuse existing enrollments endpoint for student's course list.
      const response = await fetch(`/api/enrollments/student/${studentId}`);
      const data = await response.json();

      if (response.ok) {
        const courseMap = new Map();
        (data || []).forEach((item) => {
          if (!item?.course_id) return;
          if (!courseMap.has(item.course_id)) {
            courseMap.set(item.course_id, {
              course_id: item.course_id,
              course_name: item.course_name || item.course_title || item.course_id,
            });
          }
        });
        setCourses(Array.from(courseMap.values()));
      } else {
        setError(data.error || 'Failed to fetch courses');
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError(err.message);
    }
  };

  const handleStartNewConversation = async (e) => {
    e.preventDefault();

    if (!newConvData.course_id) {
      setError('Please select a course');
      return;
    }

    try {
      // Get course details and faculty from existing course endpoint.
      const response = await fetch(`/api/courses/${newConvData.course_id}`);
      const data = await response.json();

      const facultyId = data.faculty_id || data.fac_id;
      if (!response.ok || !facultyId) {
        setError('Could not determine faculty for this course');
        return;
      }

      // Start conversation
      const convResponse = await fetch('/api/messages/start-conversation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          student_id: studentId,
          faculty_id: facultyId,
          course_id: newConvData.course_id,
          subject: newConvData.subject || 'Course Query',
        }),
      });

      const convData = await convResponse.json();

      if (convResponse.ok) {
        // Refresh conversations
        const refreshed = await fetchConversations();
        setShowNewConvDialog(false);
        setNewConvData({ course_id: '', subject: '' });
        const createdConversation = (refreshed || []).find(
          (c) => c.conversation_id === convData.conversation_id
        );
        if (createdConversation) {
          setSelectedConversation(createdConversation);
        } else if (convData.conversation_id) {
          setSelectedConversation({ conversation_id: convData.conversation_id });
        }
      } else {
        setError(convData.error || 'Failed to start conversation');
      }
    } catch (err) {
      console.error('Error starting conversation:', err);
      setError(err.message);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!messageInput.trim() || !selectedConversation) {
      return;
    }

    const messageText = messageInput.trim();
    setMessageInput('');

    try {
      const response = await fetch('/api/messages/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: selectedConversation.conversation_id,
          sender_id: studentId,
          sender_role: 'student',
          message_text: messageText,
        }),
      });

      if (response.ok) {
        // Refresh messages
        fetchMessages(selectedConversation.conversation_id);
      } else {
        setError('Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message);
    }
  };

  const handleVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setError('Voice input is not supported in this browser');
      return;
    }

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = navigator.language || 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;
    const existingText = messageInput ? `${messageInput.trim()} ` : '';
    let finalTranscript = '';
    let hadError = false;

    const mapVoiceError = (code) => {
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        return 'Microphone permission denied. Please allow mic access in browser settings.';
      }
      if (code === 'audio-capture') {
        return 'No microphone detected. Check your microphone connection.';
      }
      if (code === 'network') {
        return 'Voice service/network error. Please check internet and try again.';
      }
      if (code === 'no-speech') {
        return 'No speech detected. Please speak clearly and try again.';
      }
      if (code === 'aborted') {
        return 'Voice input stopped.';
      }
      return `Voice input failed (${code || 'unknown error'}).`;
    };

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const chunk = event.results[i][0].transcript || '';
        if (event.results[i].isFinal) {
          finalTranscript += `${chunk} `;
        } else {
          interimTranscript += chunk;
        }
      }
      const nextText = `${existingText}${finalTranscript}${interimTranscript}`.trim();
      setMessageInput(nextText);
    };

    recognition.onerror = (event) => {
      hadError = true;
      setError(mapVoiceError(event.error));
    };

    recognition.onend = () => {
      if (!hadError && finalTranscript.trim()) {
        setMessageInput(`${existingText}${finalTranscript}`.trim());
      }
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (err) {
      setIsListening(false);
      recognitionRef.current = null;
      setError('Unable to start microphone');
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;

    return date.toLocaleDateString();
  };

  return (
    <div className="student-messages-container">
      {/* Left Panel - Conversations */}
      <div className="conversations-panel">
        <div className="panel-header">
          <h2>💬 My Messages</h2>
          <button
            className="btn-new-conversation"
            onClick={() => setShowNewConvDialog(true)}
            title="Start new conversation"
          >
            ➕
          </button>
        </div>

        {/* New Conversation Dialog */}
        {showNewConvDialog && (
          <div className="new-conv-dialog">
            <div className="dialog-overlay" onClick={() => setShowNewConvDialog(false)} />
            <div className="dialog-content">
              <h3>Start New Conversation</h3>
              <form onSubmit={handleStartNewConversation}>
                <div className="form-group">
                  <label>Select Course *</label>
                  <select
                    value={newConvData.course_id}
                    onChange={(e) => setNewConvData(prev => ({
                      ...prev,
                      course_id: e.target.value,
                    }))}
                    required
                  >
                    <option value="">-- Choose a course --</option>
                    {courses.map(course => (
                      <option key={course.course_id} value={course.course_id}>
                        {course.course_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Subject (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g., Doubt about Module 3"
                    value={newConvData.subject}
                    onChange={(e) => setNewConvData(prev => ({
                      ...prev,
                      subject: e.target.value,
                    }))}
                  />
                </div>

                <div className="dialog-buttons">
                  <button type="button" className="btn-cancel" onClick={() => setShowNewConvDialog(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-start">
                    Start Conversation
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {loading && !conversations.length ? (
          <div className="loading-skeleton">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="skeleton-item" />
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div className="empty-conversations">
            <div className="empty-icon">💭</div>
            <p>No conversations yet</p>
            <p className="empty-hint">Ask your faculty a question to start</p>
          </div>
        ) : (
          <div className="conversations-list">
            {conversations.map(conv => (
              <div
                key={conv.conversation_id}
                className={`conversation-item ${
                  selectedConversation?.conversation_id === conv.conversation_id ? 'active' : ''
                } ${conv.unread_count > 0 ? 'unread' : ''}`}
                onClick={() => setSelectedConversation(conv)}
              >
                <div className="conversation-avatar">
                  {conv.faculty_name.charAt(0).toUpperCase()}
                </div>

                <div className="conversation-info">
                  <div className="info-header">
                    <h4>{conv.faculty_name}</h4>
                    <span className="timestamp">
                      {formatDate(conv.last_message_at)}
                    </span>
                  </div>

                  <p className="course-badge">{conv.course_name}</p>
                  <p className="last-message">{conv.last_message || 'No messages yet'}</p>
                </div>

                {conv.unread_count > 0 && (
                  <div className="unread-badge">{conv.unread_count}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right Panel - Chat */}
      <div className="chat-panel">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="chat-header">
              <div className="header-info">
                <h3>{selectedConversation.faculty_name}</h3>
                <p>{selectedConversation.course_name}</p>
              </div>
            </div>

            {/* Messages */}
            <div className="messages-area">
              {messages.length === 0 ? (
                <div className="empty-chat">
                  <div className="empty-icon">📝</div>
                  <h4>Start the conversation</h4>
                  <p>Ask your faculty about the course content or assignments</p>
                </div>
              ) : (
                messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`message-bubble ${msg.sender_role === 'student' ? 'sent' : 'received'
                      }`}
                  >
                    <div className="message-content">
                      <p>{msg.message_text}</p>
                      <span className="message-time">
                        {formatDate(msg.created_at)}
                      </span>
                      {msg.sender_role === 'student' && (
                        <span className={`message-status ${msg.is_read ? 'seen' : 'delivered'}`}>
                          {msg.is_read ? 'Seen' : 'Delivered'}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="message-input-form">
              <input
                type="text"
                placeholder="Ask your question..."
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                className="message-input"
                disabled={!selectedConversation}
              />
              <button
                type="submit"
                className="btn-send"
                disabled={!messageInput.trim() || !selectedConversation}
              >
                Send
              </button>
              <button
                type="button"
                className={`btn-voice ${isListening ? 'listening' : ''}`}
                onClick={handleVoiceInput}
                disabled={!selectedConversation}
                title={isListening ? 'Stop voice input' : 'Start voice input'}
              >
                {isListening ? 'Stop Mic' : 'Mic'}
              </button>
            </form>
          </>
        ) : (
          <div className="no-conversation">
            <div className="no-conv-icon">💬</div>
            <h3>No conversation selected</h3>
            <p>Select a conversation or create a new one to start messaging</p>
            <button
              className="btn-new"
              onClick={() => setShowNewConvDialog(true)}
            >
              ➕ New Conversation
            </button>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-toast">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
    </div>
  );
};

export default StudentMessages;
